import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  FireIcon,
  XMarkIcon,
} from '@heroicons/react/20/solid';

import { CalendarWeekTotals, FillStatus } from '../api/types';

// Week-level summary pills: Critical (red), At risk (yellow), Filled (green).
// Counts come live from the calendar_week totals across the visible range.
//
// ## They are also the Calendar's quick filter (GRAVY-10319)
//
// The approved prototype (`ui-prototype/shift-risk-monitoring`) makes each pill a
// button that filters the week to that fill band, mutually exclusively:
//
//   inactive pill  -> filter to that band
//   another pill   -> REPLACE the band (never combine)
//   active pill    -> clear the filter
//
// which is `applied.riskFilter = applied.riskFilter === type ? null : type` there.
// They shipped as `<span>`s with no handler, so the styling advertised an
// interaction that did nothing.
//
// ## One canonical filter state, not a second one
//
// The pills read and write `filters.fillStatus` — the SAME array Show > Fill rate
// writes, already serialized as `fill_status` and already part of the
// calendar_week query key. So synchronisation is not something kept up: there is
// only one state. Reset filters clears the pills because it clears that array,
// and the Show button's count includes a pill selection because it counts it.
//
// The consequence worth naming: a pill is active only when the selection is
// EXACTLY that one band. A multi-select made in Show > Fill rate
// (`['critical', 'at_risk']`) leaves every pill unpressed rather than lighting one
// of them up — the pills are exclusive, so they cannot honestly represent a
// combined selection. Clicking a pill from there replaces the whole selection
// with the single band, which is the prototype's behaviour and the ticket's.
//
// Counts are NOT recomputed from the filtered result: `totals` is the week's own
// distribution, so the other two bands still show what switching to them would
// find.

type Props = {
  totals: CalendarWeekTotals;
  /** The canonical Calendar fill-rate selection. */
  value: FillStatus[];
  onChange: (next: FillStatus[]) => void;
};

/**
 * The three bands, in the prototype's order. `testId` is the prototype's own
 * element id (`badge-critical` …), harvested rather than invented, so the
 * design contract and the tests name the same thing.
 */
const BANDS: Array<{
  band: FillStatus;
  testId: string;
  modifier: string;
  Icon: typeof FireIcon;
  count: (t: CalendarWeekTotals) => number;
  label: string;
}> = [
  {
    band: 'critical',
    testId: 'badge-critical',
    modifier: 'is-critical',
    Icon: FireIcon,
    count: (t) => t.critical,
    label: 'Critical',
  },
  {
    band: 'at_risk',
    testId: 'badge-atrisk',
    modifier: 'is-atrisk',
    Icon: ExclamationCircleIcon,
    count: (t) => t.at_risk,
    label: 'at risk',
  },
  {
    band: 'filled',
    testId: 'badge-filled',
    modifier: 'is-filled',
    Icon: CheckCircleIcon,
    count: (t) => t.filled,
    label: 'Filled',
  },
];

const SummaryPills = ({ totals, value, onChange }: Props) => {
  // Exactly one band, or nothing. See the note above on multi-select.
  const active = value.length === 1 ? value[0] : null;

  return (
    <div className="dashv2-summary-pills">
      {BANDS.map(({ band, testId, modifier, Icon, count, label }) => {
        const isActive = active === band;
        return (
          <button
            key={band}
            type="button"
            data-testid={testId}
            className={`dashv2-summary-pill ${modifier}${
              isActive ? ' is-active' : ''
            }`}
            // The pressed state IS the filter state, so a screen reader hears
            // the same thing the fill tells a sighted user.
            aria-pressed={isActive}
            title={`${count(totals)} ${label}`}
            onClick={() => onChange(isActive ? [] : [band])}
          >
            <Icon />
            {/* Visually hidden below 1440, where the design shows the icon
                alone (Figma `Admin-Dashboard x 1024`: a 44px pill = 12 + 20 +
                12). Kept in the DOM so the button still announces "5 at risk",
                and mirrored into `title` so the count is still recoverable on
                hover once it leaves the surface. */}
            <span className="dashv2-pill-text">
              {count(totals)} {label}
            </span>
            {/* The prototype reveals a separator and an X on the active chip, to
                say the click that follows will clear it. Decorative: the button
                itself already clears, and a nested control inside a button is
                neither valid nor reachable by keyboard. */}
            {isActive && (
              <>
                <span className="dashv2-pill-sep" aria-hidden="true" />
                <span className="dashv2-pill-x" aria-hidden="true">
                  <XMarkIcon />
                </span>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default SummaryPills;
